import React from 'react';
import { LEAN_BLOCKS } from './constants';
import { Rect, Group, Text, Circle, Label, Tag } from 'react-konva';
import Konva from 'konva';
import { injectIntl } from 'react-intl';
import { inject, observer } from 'mobx-react';
import tagMessages from '../../locales/igtagMessagesmbc.json';

// Se utiliza inyección de dependencias con intl y rootStore, y se observa con MobX
@injectIntl
@inject('rootStore')
@observer
export default class ArchitectureBlock extends React.Component {
  constructor(props) {
    super(props);
    // Estado para controlar la visibilidad del popover
    this.state = { showPopover: false };
    // Se enlaza el manejador de click y se crea la referencia para el nodo Group (para animaciones)
    this.handleClick = this.handleClick.bind(this);
    this.groupRef = React.createRef();
    this.hoverTimer = null;
    this.jumpTimer = null;
  }

  // Maneja el evento click redirigiendo a la ruta correspondiente del bloque
  handleClick() {
    const { block, rootStore, history } = this.props;
    history.push(rootStore.blockPath(block));
  }

  // Realiza una animacion de salto para resaltar el bloque
  animateJump() {
    const groupNode = this.groupRef.current;
    if (groupNode) {
      const origY = groupNode.y();
      // Animación para mover hacia arriba
      const jumpUp = new Konva.Tween({
        node: groupNode,
        duration: 0.2,
        y: origY - 10,
        easing: Konva.Easings.EaseOut,
        onFinish: () => {
          // Animación para regresar a la posición original
          const jumpDown = new Konva.Tween({
            node: groupNode,
            duration: 0.2,
            y: origY,
            easing: Konva.Easings.EaseIn
          });
          jumpDown.play();
        }
      });
      jumpUp.play();
    }
  }

  render() {
    // Extrae propiedades necesarias del componente
    const { intl, x, y, width, height, block, stepNumber } = this.props;
    const { rootStore } = this.props;
    const currentBlock = rootStore.igBusinessStore.currentBlock;
    let cards = [];
    let title = '';

    // Obtiene elementos para bloques Lean o tarjetas seleccionadas
    if (
      block === LEAN_BLOCKS.MEASURE ||
      block === LEAN_BLOCKS.LEARN ||
      block === LEAN_BLOCKS.BUILD
    ) {
      cards = rootStore.activityStore.activityEdit.getLeanBlockElements(block);
    } else {
      cards = rootStore.activityStore.activityEdit.getSelectedCardsByBlock(
        block
      );
      // Formatea el título usando traducciones de intl
      title = intl.formatMessage({ id: `IgbmcBoard-${block}` });
    }
    // Traduce el subtítulo del bloque
    const subtitle = intl.formatMessage({ id: `subtitle-${block}` });

    // Constantes para definir alturas y márgenes internos
    const titleHeight = 30;
    const cardHeight = 30;
    const marginBottomTitle = 10;
    let medVar = 15;
    const maxCards = (height - (titleHeight + marginBottomTitle)) / cardHeight;

    // Determina si el bloque tiene un estilo especial basado en el número de paso
    const isSpecial =
      stepNumber === 2 || stepNumber === 11 || stepNumber === 12;
    const defaultRectColorBackground = '#0fa69a';

    // Configuración de estilos por defecto y en caso de que el bloque esté activo
    let strokeWidth = 1;
    let borderColor = '#d0d0d0';
    let numberColor = '#d0d0d0';
    let circleColorBackground = 'white';
    let fontStyleType = 'normal';
    let headerTextColor = '#555';

    if (block === currentBlock) {
      strokeWidth = 4;
      fontStyleType = 'bold';
      if (isSpecial) {
        headerTextColor = '#666666';
        borderColor = '#6d22b0';
        numberColor = 'white';
        circleColorBackground = '#6d22b0';
      } else {
        headerTextColor = '#666666';
        borderColor = defaultRectColorBackground;
        numberColor = 'white';
        circleColorBackground = defaultRectColorBackground;
      }
    } else {
      if (isSpecial) {
        borderColor = '#C8A2C8';
        numberColor = '#C8A2C8';
        headerTextColor = '#666666';
      }
    }

    // Color del encabezado según si el bloque es actual y especial
    const activeContainerHeaderColor =
      block === currentBlock
        ? isSpecial
          ? '#6d22b0'
          : defaultRectColorBackground
        : defaultRectColorBackground;

    // Cálculo de la posición horizontal para el número central del bloque
    let textXPosition = x + width / 2 - 13;
    if (stepNumber >= 10) {
      textXPosition = x + width / 2 - 22;
    }
    if (title.length > 17) {
      medVar = 11;
    }

    // Configuración de fuentes y posiciones basada en si es dispositivo móvil
    const fontSize = this.props.isMobile ? 25 : 40;
    textXPosition = this.props.isMobile ? x + width / 2 : textXPosition;
    const textYPosition = y + height / 2;

    const subtitleFontSize = this.props.isMobile ? 7 : 15;
    const titleFontSize = this.props.isMobile ? 7 : 14;
    const subtitleY = this.props.isMobile ? 10 : 0;

    //Calcula el radio para las forma
    const radius = Math.min(width, height) / 2 - 10;
    //decide si se usa Rect o Circle stepnumber
    const isRect = stepNumber === 2 || stepNumber === 11 || stepNumber === 12;
    const mainShape = isRect ? (
      //Forma rectangular
      <Rect
        x={x + width / 2 - radius}
        y={y + height / 2 - radius}
        width={radius * 2}
        height={radius * 2}
        fill={circleColorBackground}
        stroke={borderColor}
        strokeWidth={5}
        cornerRadius={5}
      />
    ) : (
      //Forma circular
      <Circle
        x={x + width / 2}
        y={y + height / 2}
        radius={radius}
        fill={circleColorBackground}
        stroke={borderColor}
        strokeWidth={5}
      />
    );

    // Selecciona el mensaje del tag según el idioma
    const language = intl.locale && intl.locale.startsWith('es') ? 'es' : 'en';
    const tagMessage = tagMessages[language][`mod_${stepNumber}`];

    return (
      <Group
        // Ref para animaciones y manejo del grupo de elementos
        ref={this.groupRef}
        onClick={this.handleClick}
        onTap={this.handleClick}
        onMouseEnter={() => {
          // Inicia timer para mostrar popover tras 1 segundo
          this.hoverTimer = setTimeout(() => {
            this.setState({ showPopover: true });
          }, 1000);
          // Inicia timer para animar salto tras 400ms si el bloque no es el actual
          this.jumpTimer = setTimeout(() => {
            if (block !== currentBlock) {
              this.animateJump();
            }
          }, 400);
        }}
        onMouseLeave={() => {
          // Limpia los timers y oculta el popover al salir el puntero
          clearTimeout(this.hoverTimer);
          clearTimeout(this.jumpTimer);
          this.setState({ showPopover: false });
        }}
      >
        {mainShape}
        {/* Muestra el número del paso */}
        <Text
          x={
            textXPosition - (this.props.isMobile ? (stepNumber > 9 ? 3 : 1) : 0)
          }
          y={
            textYPosition -
            (this.props.isMobile ? (stepNumber > 9 ? 1 : 0) : 20)
          }
          text={stepNumber.toString()}
          fontSize={fontSize}
          fontFamily="Arial"
          fill={numberColor}
          align="center"
          verticalAlign="middle"
          offsetX={
            this.props.isMobile
              ? stepNumber.toString().length === 1
                ? fontSize * 0.25
                : fontSize * 0.45
              : 0
          }
          offsetY={
            this.props.isMobile
              ? stepNumber.toString().length === 1
                ? fontSize * 0.35
                : fontSize * 0.45
              : 0
          }
        />
        {/* Muestra el subtítulo del bloque */}
        <Text
          x={x + 10}
          y={y - 25 + subtitleY}
          width={width - 20}
          height={titleHeight}
          align="center"
          text={subtitle.toUpperCase()}
          fontSize={subtitleFontSize}
          fontFamily="Arial"
          fill={headerTextColor}
          fontStyle={fontStyleType}
        />
        {this.state.showPopover && block !== currentBlock && (
          // Si showPopover es true y el bloque no es el actual, muestra un Label con Tag y mensaje
          <Label x={x + width / 2} y={y + height + 10}>
            <Tag
              fill="white"
              stroke="black"
              strokeWidth={1}
              pointerDirection="up"
              pointerWidth={10}
              pointerHeight={10}
              cornerRadius={5}
            />
            <Text
              text={tagMessage}
              fontSize={12.5}
              fontFamily="Arial"
              fill="black"
              padding={5}
              width={120}
              align="center"
              wrap="word"
              lineHeight={1.25}
            />
          </Label>
        )}
        <Group y={5} />
        {block === currentBlock && (
          // Si el bloque es el actual, se muestra información adicional
          <Group>
            {(() => {
              if (this.props.isMobile) {
                // Layout para dispositivos móviles
                const containerX = x + 10;
                const containerWidth = width - 20;
                const headerHeight = 25;
                const headerY =
                  y + height / 2 + Math.min(width, height) / 2 + 12;
                const cardContainerHeight = 30;
                const cardContainerY = headerY + headerHeight;
                const centerX = containerX + containerWidth / 2;
                const centerY = cardContainerY + cardContainerHeight / 2;
                const extraY = title.length >= 25 ? 3 : 0;
                const titleYOffset = title.length >= 14 ? -3 - extraY : 0;
                return (
                  <>
                    <Rect
                      x={containerX}
                      y={headerY}
                      width={containerWidth}
                      height={headerHeight}
                      fill={activeContainerHeaderColor}
                      stroke={activeContainerHeaderColor}
                      strokeWidth={2}
                      cornerRadius={2}
                    />
                    <Text
                      x={x + 20}
                      y={
                        headerY +
                        headerHeight / 2 -
                        titleFontSize / 2 +
                        titleYOffset
                      }
                      width={width - 40}
                      align="center"
                      text={title}
                      fontSize={titleFontSize}
                      fontFamily="Arial"
                      fill="white"
                      fontStyle="normal"
                    />
                    <Rect
                      x={containerX}
                      y={cardContainerY}
                      width={containerWidth}
                      height={cardContainerHeight}
                      fill="white"
                      stroke={borderColor}
                      strokeWidth={2}
                      cornerRadius={2}
                    />
                    {cards.length > 0 && (
                      <Text
                        x={centerX - 8}
                        y={centerY - 12}
                        text="..."
                        fontSize={18}
                        fontFamily="Helvetica"
                        fill="#777"
                        align="center"
                      />
                    )}
                  </>
                );
              } else {
                // Layout para escritorio
                const containerHeight = 70 + Math.min(cards.length, 2) * 30;
                return (
                  <>
                    <Rect
                      x={x + 10}
                      y={y + height / 2 + Math.min(width, height) / 2 + 10}
                      width={width - 20}
                      height={containerHeight}
                      fill="white"
                      stroke={borderColor}
                      strokeWidth={2}
                      cornerRadius={5}
                    />
                    <Rect
                      x={x + 10}
                      y={y + height / 2 + Math.min(width, height) / 2 + 10}
                      width={width - 20}
                      height={30}
                      fill={activeContainerHeaderColor}
                      stroke={activeContainerHeaderColor}
                      strokeWidth={2}
                      cornerRadius={5}
                    />
                    <Text
                      x={x + 20}
                      y={
                        y +
                        height / 2 +
                        Math.min(width, height) / 2 +
                        medVar +
                        2
                      }
                      width={width - 40}
                      align="center"
                      text={title}
                      fontSize={titleFontSize}
                      fontFamily="Arial"
                      fill="white"
                      fontStyle="normal"
                    />
                    <Group>
                      {cards.map((card, i) => {
                        if (i + 1 < maxCards) {
                          const baseY =
                            y + height / 2 + Math.min(width, height) / 2 + 45;
                          return (
                            <Group key={i}>
                              <Rect
                                x={x + 15}
                                y={baseY + i * 30}
                                width={width - 30}
                                height={25}
                                fill="white"
                                stroke="#ccc"
                                strokeWidth={1}
                                cornerRadius={2}
                              />
                              <Text
                                x={x + 20}
                                y={baseY + 5 + i * 30}
                                width={width - 40}
                                align="left"
                                text={card.description || card.name || card}
                                fontSize={14}
                                lineHeight={1.3}
                                fontFamily="Helvetica"
                                fill="#333"
                                wrap="none"
                              />
                            </Group>
                          );
                        }
                        return null;
                      })}
                      {cards.length >= maxCards && (
                        <Text
                          x={x + 10}
                          y={
                            y +
                            height / 2 +
                            Math.min(width, height) / 2 +
                            33 +
                            maxCards * 30
                          }
                          width={width - 20}
                          align="center"
                          text="..."
                          fontSize={18}
                          fontFamily="Helvetica"
                          fill="#777"
                        />
                      )}
                    </Group>
                  </>
                );
              }
            })()}
          </Group>
        )}
      </Group>
    );
  }
}
